import { useSyncExternalStore } from "react";
import { WorkflowApiError } from "@/api/workflows";

export type ComposerRunStatus = "running" | "status_unknown" | "unavailable" | "completed" | "failed" | "cancelled" | "timed_out";
export interface ComposerRun {
  taskId: string;
  actionId: string;
  sessionId: string;
  status: ComposerRunStatus;
}

const terminal = new Set<ComposerRunStatus>(["completed", "failed", "cancelled", "timed_out", "unavailable"]);
const runs = new Map<string, ComposerRun>();
const listeners = new Set<() => void>();
let revision = 0;

function saveSession(sessionId: string) {
  try {
    const active = [...runs.values()]
      .filter((run) => run.sessionId === sessionId && !terminal.has(run.status))
      .map(({ taskId, actionId }) => ({ taskId, actionId }));
    window.sessionStorage.setItem(`composer-action-runs:${sessionId}`, JSON.stringify(active));
  } catch {
    // Storage is optional; the loaded transcript still restores visible runs.
  }
}

function publish() {
  revision += 1;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getRevision() { return revision; }
function serverRevision() { return 0; }

export function useComposerRunRevision() {
  return useSyncExternalStore(subscribe, getRevision, serverRevision);
}

export function composerRun(taskId: string) { return runs.get(taskId); }

export function composerRunsForSession(sessionId: string) {
  return [...runs.values()].filter((run) => run.sessionId === sessionId);
}

export function actionIsRunning(sessionId: string, actionId: string) {
  return [...runs.values()].some((run) => run.sessionId === sessionId && run.actionId === actionId && run.status === "running");
}

export function restoreComposerRuns(
  sessionId: string,
  fetchStatus: (taskId: string) => Promise<string>,
) {
  try {
    const saved: unknown = JSON.parse(window.sessionStorage.getItem(`composer-action-runs:${sessionId}`) ?? "[]");
    if (!Array.isArray(saved)) return;
    for (const candidate of saved) {
      if (candidate && typeof candidate === "object"
        && typeof candidate.taskId === "string" && typeof candidate.actionId === "string") {
        trackComposerRun(candidate.taskId, candidate.actionId, sessionId, () => fetchStatus(candidate.taskId));
      }
    }
  } catch {
    // Old or unavailable browser storage must not hide the composer.
  }
}

export function trackComposerRun(
  taskId: string,
  actionId: string,
  sessionId: string,
  fetchStatus: () => Promise<string>,
) {
  if (!taskId || runs.has(taskId)) return;
  runs.set(taskId, { taskId, actionId, sessionId, status: "running" });
  saveSession(sessionId);
  publish();
  let failures = 0;
  const poll = async () => {
    let next: ComposerRunStatus;
    try {
      const status = (await fetchStatus()).toLowerCase();
      next = terminal.has(status as ComposerRunStatus) ? status as ComposerRunStatus : "running";
      failures = 0;
    } catch (error) {
      next = error instanceof WorkflowApiError && error.status >= 400 && error.status < 500
        ? "unavailable" : "status_unknown";
      failures += 1;
    }
    const current = runs.get(taskId);
    if (!current) return;
    if (current.status !== next) {
      runs.set(taskId, { ...current, status: next });
      saveSession(current.sessionId);
      publish();
    }
    if (!terminal.has(next)) {
      window.setTimeout(() => { void poll(); }, next === "status_unknown" ? Math.min(5000 * 2 ** Math.min(failures, 4), 60000) : 5000);
    }
  };
  void poll();
}
